import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { Project, Department, StepStatus } from '../../types';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { DeptBadge } from '../../components/ui/DeptBadge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import {
  ChevronLeft, Clock, AlertTriangle, Check, Play, ChevronRight,
  User, Calendar, FileText, Activity, Package, Info, Layers,
} from 'lucide-react';

// ─── Design tokens ───────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// ─── Dept config ──────────────────────────────────────────────────────────────

const DEPT_COLORS: Record<Department, string> = {
  gestor: '#3b82f6',
  financiero: '#8b5cf6',
  imprenta: '#ec4899',
  dtf: '#f97316',
  almacen: '#22c55e',
  embalaje: '#14b8a6',
};

const DEPT_ICONS: Record<Department, string> = {
  gestor: '📋',
  financiero: '💰',
  imprenta: '🖨️',
  dtf: '🎨',
  almacen: '📦',
  embalaje: '📫',
};

const DEPT_ROLES: Record<Department, string> = {
  gestor: 'ROLE_GESTOR',
  financiero: 'ROLE_FINANCIERO',
  imprenta: 'ROLE_IMPRENTA',
  dtf: 'ROLE_DTF',
  almacen: 'ROLE_ALMACEN',
  embalaje: 'ROLE_EMBALAJE',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#22c55e', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente',
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK: Project = {
  id: 1, reference: 'PRJ-001', title: 'Camisetas DTF Temporada Verano',
  description: 'Producción de 200 camisetas con transferencia DTF de alta calidad para la colección de verano de Moda Express. Tamaños: S, M, L, XL. Diseño proporcionado por el cliente en formato SVG.',
  status: 'in_progress', currentDepartment: 'dtf',
  notifications: { email: true, onStateChange: true },
  client: { id: 1, name: 'Carlos García', company: 'Moda Express SL' },
  dueDate: new Date(Date.now() + 2 * 86400000).toISOString(),
  createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
  progress: 45,
  steps: [
    { id: 1, department: 'gestor',   orderIndex: 0, status: 'done',        data: {}, notes: 'Proyecto aprobado por dirección. Presupuesto confirmado.', completedAt: new Date(Date.now() - 4 * 86400000).toISOString() },
    { id: 2, department: 'financiero', orderIndex: 1, status: 'done',      data: {}, notes: 'Factura proforma enviada al cliente. Pago recibido.', completedAt: new Date(Date.now() - 3 * 86400000).toISOString() },
    { id: 3, department: 'dtf',      orderIndex: 2, status: 'in_progress', data: {}, startedAt: new Date(Date.now() - 1 * 86400000).toISOString() },
    { id: 4, department: 'embalaje', orderIndex: 3, status: 'pending',     data: {} },
  ],
};

const MOCK_ACTIVITIES = [
  { id: 1, type: 'step_done',       dept: 'gestor',    text: 'Paso completado: Gestor',     date: new Date(Date.now() - 4 * 86400000).toISOString(), user: 'Ana López' },
  { id: 2, type: 'step_done',       dept: 'financiero', text: 'Paso completado: Financiero', date: new Date(Date.now() - 3 * 86400000).toISOString(), user: 'Juan Martín' },
  { id: 3, type: 'step_started',    dept: 'dtf',       text: 'Iniciado: DTF',                date: new Date(Date.now() - 86400000).toISOString(), user: 'María Sanz' },
  { id: 4, type: 'project_created', dept: 'gestor',    text: 'Proyecto creado',              date: new Date(Date.now() - 5 * 86400000).toISOString(), user: 'Sistema' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDatetime(iso: string) {
  return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isOverdue(iso: string) { return new Date(iso).getTime() < Date.now(); }

// ─── Step action panel ────────────────────────────────────────────────────────

function StepActionPanel({ project, step, onUpdate }: any) {
  const [notes, setNotes] = useState(step.notes || '');
  const [materials, setMaterials] = useState<{ name: string; qty: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qc = useQueryClient();

  const handleAction = async (nextStatus: StepStatus) => {
    setSaving(true);
    setError(null);
    try {
      await projectsApi.updateStep(project.id, step.id, {
        status: nextStatus,
        notes,
        data: { materials: materials.filter(m => m.name.trim()) },
      });
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      onUpdate();
    } catch {
      setError('Error al actualizar el paso. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const addMaterial = () => setMaterials(prev => [...prev, { name: '', qty: '' }]);
  const updateMaterial = (idx: number, field: 'name' | 'qty', value: string) =>
    setMaterials(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  const removeMaterial = (idx: number) => setMaterials(prev => prev.filter((_, i) => i !== idx));

  const inputStyle: React.CSSProperties = {
    background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 8,
    color: C.textPrimary, fontSize: 12, padding: '8px 10px',
    fontFamily: 'DM Sans, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 16, padding: 16, background: 'rgba(249,115,22,0.04)', border: `1px solid rgba(249,115,22,0.2)`, borderRadius: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.orange, letterSpacing: '0.06em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 14 }}>TU TURNO</div>

      {/* Notes */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: C.textMuted, letterSpacing: '0.04em', fontFamily: 'JetBrains Mono, monospace', display: 'block', marginBottom: 6 }}>NOTAS</label>
        <textarea
          style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
          placeholder="Añadir notas sobre este paso..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>

      {/* Materials */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ fontSize: 11, color: C.textMuted, letterSpacing: '0.04em', fontFamily: 'JetBrains Mono, monospace' }}>MATERIALES USADOS</label>
          <button onClick={addMaterial} style={{ background: 'rgba(249,115,22,0.1)', border: `1px solid rgba(249,115,22,0.3)`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: C.orange, fontSize: 11, fontFamily: 'DM Sans, sans-serif' }}>+ Añadir</button>
        </div>
        {materials.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Sin materiales registrados</div>
        ) : materials.map((m, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input style={{ ...inputStyle, flex: 2 }} placeholder="Material..." value={m.name} onChange={e => updateMaterial(idx, 'name', e.target.value)} />
            <input style={{ ...inputStyle, flex: 1 }} placeholder="Cantidad" value={m.qty} onChange={e => updateMaterial(idx, 'qty', e.target.value)} />
            <button onClick={() => removeMaterial(idx)} style={{ background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: C.error }}>✕</button>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 6, fontSize: 12, color: C.error, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        {step.status === 'pending' && (
          <button onClick={() => handleAction('in_progress')} disabled={saving} style={{
            flex: 1, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: '#1e3a5f', color: '#60a5fa', fontSize: 12, fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <Play size={13} fill="#60a5fa" /> Iniciar
          </button>
        )}
        {step.status === 'in_progress' && (
          <button onClick={() => handleAction('done')} disabled={saving} style={{
            flex: 1, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? '#1a2a1a' : 'linear-gradient(135deg, #22c55e, #16a34a)',
            color: saving ? C.textMuted : '#fff', fontSize: 12, fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            boxShadow: saving ? 'none' : '0 4px 12px rgba(34,197,94,0.3)',
          }}>
            {saving ? <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Check size={13} />}
            {saving ? 'Guardando...' : 'Completar'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Flow tab ─────────────────────────────────────────────────────────────────

function FlowTab({ project, canAct }: { project: Project; canAct: (dept: Department) => boolean }) {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {project.steps.sort((a, b) => a.orderIndex - b.orderIndex).map((step, idx) => {
        const isActive = step.status === 'in_progress';
        const isDone = step.status === 'done';
        const isPending = step.status === 'pending';
        const deptColor = DEPT_COLORS[step.department];
        const showAction = canAct(step.department) && (step.status === 'pending' || step.status === 'in_progress');

        return (
          <div key={step.id} style={{ display: 'flex', gap: 16 }}>
            {/* Timeline line */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: isDone ? `${C.success}22` : isActive ? `${deptColor}22` : '#1a1a2a',
                border: isDone ? `2px solid ${C.success}` : isActive ? `2px solid ${deptColor}` : `2px solid ${C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18,
                boxShadow: isActive ? `0 0 16px ${deptColor}40` : 'none',
                transition: 'all 0.3s',
              }}>
                {isDone ? <Check size={16} color={C.success} /> : <span>{DEPT_ICONS[step.department]}</span>}
              </div>
              {idx < project.steps.length - 1 && (
                <div style={{ width: 2, flex: 1, minHeight: 24, background: isDone ? `${C.success}40` : `${C.border}`, marginTop: 2 }} />
              )}
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              padding: 16,
              background: isActive ? `${deptColor}08` : C.card,
              border: isActive ? `1px solid ${deptColor}40` : `1px solid ${C.border}`,
              borderRadius: 12,
              marginBottom: idx < project.steps.length - 1 ? 8 : 0,
              transition: 'all 0.3s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? deptColor : isDone ? C.textSecondary : C.textPrimary, fontFamily: 'DM Sans, sans-serif' }}>
                    {step.orderIndex + 1}. {step.department.charAt(0).toUpperCase() + step.department.slice(1)}
                  </span>
                  {isActive && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: `${deptColor}22`, color: deptColor, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>EN PROCESO</span>
                  )}
                  {isDone && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: `${C.success}22`, color: C.success, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>COMPLETADO</span>
                  )}
                  {isPending && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: `${C.border}`, color: C.textMuted, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>PENDIENTE</span>
                  )}
                </div>
                <DeptBadge dept={step.department} size="xs" />
              </div>

              {step.notes && (
                <div style={{ fontSize: 12, color: C.textSecondary, padding: '8px 12px', background: '#0a0a0f', borderRadius: 8, marginBottom: 8, lineHeight: 1.5 }}>
                  {step.notes}
                </div>
              )}

              {/* Timestamps */}
              <div style={{ display: 'flex', gap: 16 }}>
                {step.startedAt && (
                  <div style={{ fontSize: 10, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Play size={9} /> Iniciado: {formatDate(step.startedAt)}
                  </div>
                )}
                {step.completedAt && (
                  <div style={{ fontSize: 10, color: C.success, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={9} /> Completado: {formatDate(step.completedAt)}
                  </div>
                )}
              </div>

              {/* Action panel for current user */}
              {showAction && (
                <StepActionPanel
                  project={project}
                  step={step}
                  onUpdate={() => setRefreshKey(k => k + 1)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Info tab ─────────────────────────────────────────────────────────────────

function InfoTab({ project, canEdit, isMobile }: { project: Project; canEdit: boolean; isMobile: boolean }) {
  const Row = ({ label, value }: { label: string; value?: string | null }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, color: C.textMuted, minWidth: isMobile ? 100 : 140 }}>{label}</span>
      <span style={{ fontSize: 12, color: C.textPrimary, textAlign: 'right', flex: 1 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
      {/* Client info */}
      <div style={{ padding: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={14} color={C.orange} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, fontFamily: 'Syne, sans-serif' }}>Cliente</span>
        </div>
        <Row label="Nombre" value={project.client?.name} />
        <Row label="Empresa" value={(project.client as any)?.company} />
        <Row label="Email" value={(project.client as any)?.email} />
        <Row label="Teléfono" value={(project.client as any)?.phone} />
        <Row label="Ciudad" value={(project.client as any)?.city} />
      </div>

      {/* Project info */}
      <div style={{ padding: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={14} color={C.orange} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, fontFamily: 'Syne, sans-serif' }}>Detalles</span>
        </div>
        {project.description && (
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.6, marginBottom: 16, padding: '10px 12px', background: '#0a0a0f', borderRadius: 8 }}>
            {project.description}
          </div>
        )}
        <Row label="Referencia" value={project.reference} />
        <Row label="Creado" value={formatDate(project.createdAt)} />
        <Row label="Fecha límite" value={project.dueDate ? formatDate(project.dueDate) : ''} />
        {project.completedAt && <Row label="Completado" value={formatDate(project.completedAt)} />}
        {canEdit && (
          <button style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.orange}`, background: 'rgba(249,115,22,0.08)', color: C.orange, fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
            Editar proyecto
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Activity tab ─────────────────────────────────────────────────────────────

function ActivityTab({ activities }: { activities: typeof MOCK_ACTIVITIES }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {activities.map((act, idx) => (
        <div key={act.id} style={{ display: 'flex', gap: 14, position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: act.type === 'step_done' ? `${C.success}22` : act.type === 'step_started' ? `${C.orange}22` : `${C.border}`,
              border: `2px solid ${act.type === 'step_done' ? C.success : act.type === 'step_started' ? C.orange : C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14,
            }}>
              {act.type === 'step_done' ? <Check size={13} color={C.success} /> : act.type === 'step_started' ? <Play size={13} color={C.orange} fill={C.orange} /> : <Activity size={13} color={C.textMuted} />}
            </div>
            {idx < activities.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 20, background: C.border }} />}
          </div>
          <div style={{ flex: 1, paddingBottom: 20 }}>
            <div style={{ fontSize: 13, color: C.textPrimary, fontFamily: 'DM Sans, sans-serif', fontWeight: 500, marginBottom: 2 }}>{act.text}</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <span style={{ fontSize: 11, color: C.textMuted }}>{act.user}</span>
              <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>{formatDatetime(act.date)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main ProjectDetailPage ───────────────────────────────────────────────────

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuthStore();
  const [tab, setTab] = useState<'flujo' | 'info' | 'actividad'>('flujo');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiProject, isLoading } = useQuery({
    queryKey: ['project', Number(id)],
    queryFn: () => projectsApi.get(Number(id)),
    retry: false,
  });

  const project: Project = (apiProject && apiProject.id) ? apiProject : MOCK;
  const overdue = project.dueDate && isOverdue(project.dueDate) && project.status !== 'done';

  const canAct = (dept: Department) =>
    hasRole(DEPT_ROLES[dept] as any) || hasRole('ROLE_ADMIN');

  const canEdit = hasRole('ROLE_GESTOR') || hasRole('ROLE_ADMIN');

  const TABS = [
    { key: 'flujo',     label: 'Flujo',     icon: Layers },
    { key: 'info',      label: 'Info',      icon: Info },
    { key: 'actividad', label: 'Actividad', icon: Activity },
  ] as const;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: C.bg }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Back */}
      <button onClick={() => navigate('/gestor')} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: C.textSecondary, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginBottom: 24 }}>
        <ChevronLeft size={15} /> Volver a Proyectos
      </button>

      {/* Header card */}
      <div style={{ padding: isMobile ? '16px' : '24px 28px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', marginBottom: 6 }}>{project.reference}</div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 20 : 26, color: '#fff', margin: '0 0 10px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            {project.title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <StatusBadge status={project.status} />
            {(project as any).priority && (
              <span style={{
                padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: `${PRIORITY_COLORS[(project as any).priority] || C.border}22`,
                color: PRIORITY_COLORS[(project as any).priority] || C.textMuted,
                border: `1px solid ${PRIORITY_COLORS[(project as any).priority] || C.border}40`,
              }}>
                {PRIORITY_LABELS[(project as any).priority] || 'Normal'}
              </span>
            )}
            {project.client && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.textSecondary }}>
                <User size={12} /> {project.client.company || project.client.name}
              </span>
            )}
            {project.dueDate && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: overdue ? C.error : C.textSecondary }}>
                {overdue ? <AlertTriangle size={12} /> : <Calendar size={12} />}
                {formatDate(project.dueDate)}
              </span>
            )}
          </div>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <ProgressBar value={project.progress} height={6} />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.orange, fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}>{project.progress}%</span>
        </div>

        {/* Step mini flow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 16, overflowX: 'auto', paddingBottom: 2 }}>
          {project.steps.sort((a, b) => a.orderIndex - b.orderIndex).map((step, idx) => (
            <React.Fragment key={step.id}>
              {idx > 0 && <ChevronRight size={12} color={C.border} style={{ flexShrink: 0 }} />}
              <div style={{
                padding: '4px 10px', borderRadius: 6, flexShrink: 0,
                background: step.status === 'done' ? `${C.success}22` : step.status === 'in_progress' ? `${DEPT_COLORS[step.department]}22` : '#1a1a2a',
                border: `1px solid ${step.status === 'done' ? C.success : step.status === 'in_progress' ? DEPT_COLORS[step.department] : C.border}40`,
                color: step.status === 'done' ? C.success : step.status === 'in_progress' ? DEPT_COLORS[step.department] : C.textMuted,
                fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
              }}>
                {step.status === 'done' && '✓ '}{step.department}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, width: isMobile ? '100%' : 'fit-content' }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 0 : 7,
              padding: isMobile ? '10px 8px' : '8px 18px', borderRadius: 9, border: 'none',
              background: tab === key ? 'rgba(249,115,22,0.15)' : 'transparent',
              color: tab === key ? C.orange : C.textSecondary,
              fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: tab === key ? 600 : 400,
              transition: 'all 0.15s',
              flex: isMobile ? 1 : undefined,
            }}
          >
            <Icon size={14} />
            {!isMobile && <span style={{ marginLeft: 7 }}>{label}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'flujo' && <FlowTab project={project} canAct={canAct} />}
      {tab === 'info' && <InfoTab project={project} canEdit={canEdit} isMobile={isMobile} />}
      {tab === 'actividad' && <ActivityTab activities={MOCK_ACTIVITIES} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
